import {
  Coordinates,
  GameSetup,
  GameState,
  Tile,
  Word,
} from '../../../../types/types';

function getGameStateFromSetup(gameSetup: GameSetup): GameState {
  return {
    gridSize: getGridSize(gameSetup.words),
    tiles: createTileDictionary(gameSetup.words, gameSetup.startCoordinates),
    pathLetters: gameSetup.pathString.split(''),
    path: [gameSetup.startCoordinates],
    moveCount: 0,
    hintsRemaining: 0,
  };
}

function getGridSize(words: Word[]): Coordinates {
  let maxWidth = 0;

  words.forEach((word) => {
    const wordWidth = word.offset + word.text.length;
    maxWidth = wordWidth > maxWidth ? wordWidth : maxWidth;
  });

  return { x: maxWidth, y: words.length };
}

function tryMoveInDirection(state: GameState, direction: Cardinal): GameState {
  const headCoords =
    state.path.length > 0 ? state.path[state.path.length - 1] : undefined;
  if (headCoords == undefined) {
    return state;
  }

  const newCoords = coordinateInDirection(headCoords, direction);
  const head1Coords =
    state.path.length > 1 ? state.path[state.path.length - 2] : undefined;
  const isMovingBackwards =
    head1Coords != undefined && areCoordinatesEqual(newCoords, head1Coords);

  const tileAtCoordinate = state.tiles[getTileKey(newCoords)];

  if (
    !isMovingBackwards &&
    (tileAtCoordinate == undefined || tileAtCoordinate.guess != undefined)
  ) {
    return state;
  }

  return tryMove(state, newCoords);
}

function tryMove(currentGameState: GameState, move: Coordinates): GameState {
  let state = JSON.parse(JSON.stringify(currentGameState));
  const moveKey = getTileKey(move);
  const tileAtCoordinate = state.tiles[moveKey];
  const lastTileCoords =
    state.path.length > 0 ? state.path[state.path.length - 1] : undefined;

  //Check we aren't trying to move outside the grid
  if (tileAtCoordinate == undefined || lastTileCoords == undefined) {
    return state;
  }

  if (tileAtCoordinate.guess != undefined) {
    if (state.path.length <= 1) {
      return state;
    }

    while (
      state.path.length > 1 &&
      !areCoordinatesEqual(state.path[state.path.length - 1], move)
    ) {
      state = moveBackward(state, state.path[state.path.length - 1]);
    }

    state.pathLetters = getStringPath(state);

    return state;
  }

  const path = getValidMovesBetweenPoints(state, lastTileCoords, move);

  path.forEach((c) => {
    state = moveForward(state, c);
  });

  state.pathLetters = getStringPath(state);
  return state;
}

function moveForward(state: GameState, move: Coordinates): GameState {
  const tileAtCoordinate = state.tiles[getTileKey(move)];

  const partialRecord: Record<string, Tile> = {};
  partialRecord[getTileKey(move)] = {
    ...tileAtCoordinate,
    guess: tileAtCoordinate.value,
  };

  state.tiles = { ...state.tiles, ...partialRecord };
  state.path = [...state.path, move];
  state.moveCount++;

  return state;
}

function moveBackward(state: GameState, move: Coordinates): GameState {
  const tileAtCoordinate = state.tiles[getTileKey(move)];

  const partialRecord: Record<string, Tile> = {};
  partialRecord[getTileKey(move)] = { ...tileAtCoordinate, guess: undefined };

  state.tiles = { ...state.tiles, ...partialRecord };
  state.path.pop();

  return state;
}

function createTileDictionary(
  words: Word[],
  startCoordinates: Coordinates,
): Record<string, Tile> {
  const dictionary: Record<string, Tile> = {};

  words.forEach((word, wordIndex) => {
    const letters = word.text.split('');
    letters.forEach((letter, letterIndex) => {
      const tile: Tile = {
        guess: undefined,
        value: letter,
        coordinates: {
          x: letterIndex + word.offset,
          y: wordIndex,
        },
      };
      dictionary[getTileKey(tile.coordinates)] = tile;
    });
  });

  dictionary[getTileKey(startCoordinates)].guess =
    dictionary[getTileKey(startCoordinates)].value;
  return dictionary;
}

function getTileKey(coordinates: Coordinates): string {
  return `${coordinates.x},${coordinates.y}`;
}

function areCoordinatesEqual(a: Coordinates, b: Coordinates): boolean {
  return a.x == b.x && a.y == b.y;
}

export function areCoordinatesAdjacent(
  a: Coordinates,
  b: Coordinates,
): boolean {
  const xdif = Math.abs(a.x - b.x);
  const ydif = Math.abs(a.y - b.y);
  return xdif + ydif === 1;
}

function getValidMovesBetweenPoints(
  gameState: GameState,
  from: Coordinates,
  to: Coordinates,
): Coordinates[] {
  const array: Coordinates[] = [];

  if (areCoordinatesAdjacent(from, to)) {
    array.push(to);
  } else {
    if (from.y == to.y && from.x != to.x) {
      const direction = from.x - to.x > 0 ? -1 : 1;
      for (let i = from.x + direction; i != to.x + direction; i += direction) {
        array.push({ x: i, y: from.y });
      }
    }

    if (from.x == to.x && from.y != to.y) {
      const direction = from.y - to.y > 0 ? -1 : 1;
      for (let i = from.y + direction; i != to.y + direction; i += direction) {
        array.push({ y: i, x: from.x });
      }
    }
  }

  //Assumes we're trying to move into an empty space
  for (const c of array) {
    const tile = gameState.tiles[getTileKey(c)];
    if (tile == undefined || tile.guess !== undefined) {
      return [];
    }
  }

  return array;
}

function getStringPath(gameState: GameState) {
  const letters: string[] = [];

  for (const c of gameState.path) {
    const tile = gameState.tiles[getTileKey(c)];
    letters.push(tile.value);
  }

  return letters;
}

function coordinateInDirection(
  startCoordinates: Coordinates,
  direction: Cardinal,
): Coordinates {
  switch (direction) {
    case Cardinal.North:
      return { x: startCoordinates.x, y: startCoordinates.y - 1 };
    case Cardinal.South:
      return { x: startCoordinates.x, y: startCoordinates.y + 1 };
    case Cardinal.East:
      return { x: startCoordinates.x + 1, y: startCoordinates.y };
    case Cardinal.West:
      return { x: startCoordinates.x - 1, y: startCoordinates.y };
  }
}

export enum Cardinal {
  North,
  South,
  East,
  West,
}

function isGameOver(gameState: GameState): boolean {
  return gameState.path.length === Object.keys(gameState.tiles).length;
}

export {
  coordinateInDirection,
  isGameOver,
  getTileKey,
  getGameStateFromSetup,
  tryMove,
  getValidMovesBetweenPoints,
  tryMoveInDirection,
  getStringPath,
};
